import CONSTANTS from 'src/utils/constants';
import {
    CDTSegmentType,
    ColumnDataType,
    ColumnDef,
    ColumnSourceType,
    CreateSegmentDataTableRequest,
    LedgerType,
    PeriodBehavior,
    PeriodType,
    SegmentPullCriteria,
} from 'src/models/segment-cdt/SegmentCdtTypes';
import { CreateWizardState, SegmentColumnDraft } from 'src/services/segment-cdt/SegmentTablesState';

/**
 * The period-type options a GL-pulling column offers, shared by the two forms that collect one
 * (GlPullColumnForm and DerivedColumnForm's balance flavour) so the label wording cannot drift between them.
 */
export const PERIOD_OPTIONS = [
    { value: PeriodType.YTD, label: 'YTD Calendar Year' },
    { value: PeriodType.MTD, label: 'MTD' },
    { value: PeriodType.YTDA, label: 'YTD (Arrears)' },
    { value: PeriodType.MTDA, label: 'MTD (Arrears)' },
];

/**
 * Validate the GL-pull criteria a PULL_GL or DERIVED_BAL column carries: a ledger and a period are required;
 * Company and GL Account are the required data-key dimensions; LG Code is valid only under a Secondary
 * ledger. Returns an error message, or undefined when valid.
 *
 * <p>These checks now duplicate a real server check rather than standing alone: the data-key persistence
 * follow-on has landed, so `SegmentColumnConfigValidator` validates the dimensions, the LG-Code gating and
 * the custom-rollup shape server-side as well. They are kept because client-side validation is what gives
 * the user an inline message instead of a round-trip failure, but they are no longer the only backstop.
 */
function validatePullCriteria(pc: SegmentPullCriteria): string | undefined {
    if (!pc.periodType) {
        return 'Period is required.';
    }
    if (!pc.ledgerType) {
        return 'Ledger type is required.';
    }
    const dk = pc.dataKeys || {};
    if (!(dk.companies && dk.companies.length)) {
        return 'At least one Company data key is required.';
    }
    if (!(dk.glAccounts && dk.glAccounts.length)) {
        return 'At least one GL Account data key is required.';
    }
    if (dk.lgCodes && dk.lgCodes.length && pc.ledgerType !== LedgerType.SECONDARY) {
        return 'LG Code data keys are only valid under a Secondary ledger.';
    }
    return undefined;
}

/** The stored pull-criteria for a GL-pulling column: ledger + period + the non-empty data-key dimensions. */
function buildPullCriteria(pc: SegmentPullCriteria): SegmentPullCriteria {
    return {
        ledgerType: pc.ledgerType,
        periodType: pc.periodType,
        dataKeys: pc.dataKeys ?? {},
    };
}

/**
 * Pure helpers for the create wizard — kept out of the components so payload building and validation
 * are unit-testable without mounting. All mirror the server-enforced rules (CR-299438935).
 */

/**
 * A columnId derived from the display name: alphanumerics kept, each run of anything else collapsed to `_`.
 *
 * Leading and trailing underscores are deliberately KEPT. Stripping them made distinct names collide - "Rate"
 * and "Rate %" both produced `Rate` - which then had to be refused, so a user was told to rename a column
 * whose name was already unique. The id is internal (the display name is what a user sees and what the upload
 * template heads its columns with), so a trailing `_` costs nothing.
 *
 * `#` cannot survive because only alphanumerics do, which the stored sort-key grammar requires:
 * `Constants.formatValidationSortKey` rejects a columnId containing the `#` delimiter.
 */
export function toColumnId(displayName: string): string {
    return (displayName || '')
        .trim()
        .replace(/[^a-zA-Z0-9]+/g, '_');
}

/**
 * `toColumnId`, made unique against the ids already in use by suffixing `_2`, `_3` and so on.
 *
 * Collapsing to `_` is lossy, so two unique display names can still derive the same id ("Rate %" and
 * "Rate/"). Refusing the second one is the wrong answer: the name the user typed is unique, and the collision
 * is an artefact of how the id is spelled. The suffix is applied to the id only, never to the display name.
 *
 * Returns an empty string when the name carries no alphanumeric at all, which the caller reports.
 */
export function uniqueColumnId(displayName: string, existingIds: string[] = []): string {
    const base = toColumnId(displayName);
    if (!/[a-zA-Z0-9]/.test(base)) {
        return '';
    }
    const taken = new Set(existingIds.filter((id) => !!id));
    if (!taken.has(base)) {
        return base;
    }
    // Bounded by the number of columns plus one, so a candidate is always free before the loop ends.
    for (let n = 2; n <= taken.size + 2; n++) {
        const candidate = `${base}_${n}`;
        if (!taken.has(candidate)) {
            return candidate;
        }
    }
    return base;
}

/** The table name is valid when it starts with the CDT prefix, has content after it, and uses only [A-Za-z0-9_]. */
export function isValidTableName(name: string): boolean {
    const prefix = CONSTANTS.CUSTOM_DATA_TABLE_PREFIX;
    if (!name || !name.startsWith(prefix) || name.length <= prefix.length) {
        return false;
    }
    return /^[A-Za-z0-9_]+$/.test(name);
}

/**
 * The COA segment name (`getCustomCOADefinitionsForSegment`'s argument) for a given table segment.
 * That API keys off the UI segment name — the same value MagicBox passes (e.g. 'Company', 'Cost Center').
 * `ENTITY` maps to COMPANY, since the COA mapping has no distinct ENTITY entry. Null until a segment is
 * chosen, so callers can leave the dropdown disabled with a "select a segment first" hint.
 */
export function segmentCoaName(segmentType?: CDTSegmentType): string | null {
    switch (segmentType) {
        case CDTSegmentType.COST_CENTER:
            return CONSTANTS.COA_SEGMENT_MAPPING.COST_CENTER.UI;
        case CDTSegmentType.ENTITY:
            return CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI;
        case CDTSegmentType.LOCATION:
            return CONSTANTS.COA_SEGMENT_MAPPING.LOCATION.UI;
        case CDTSegmentType.PRODUCT_LINE:
            return CONSTANTS.COA_SEGMENT_MAPPING.PRODUCT_LINE.UI;
        case CDTSegmentType.CHANNEL:
            return CONSTANTS.COA_SEGMENT_MAPPING.CHANNEL.UI;
        case CDTSegmentType.PROJECT:
            return CONSTANTS.COA_SEGMENT_MAPPING.PROJECT.UI;
        default:
            return null;
    }
}

/**
 * Human label for a period type, resolved through `PERIOD_OPTIONS` so the read-only views word it exactly as
 * the form that collected it. An unrecognised value passes through verbatim rather than being blanked: it is
 * stored data, and hiding it would make a contract addition look like missing configuration.
 */
export function periodLabel(periodType?: PeriodType): string {
    if (!periodType) {
        return '';
    }
    return PERIOD_OPTIONS.find((o) => o.value === periodType)?.label ?? String(periodType);
}

/**
 * Human label for a ledger type. The wording is `DATA_SOURCE_TYPES`', matching the ledger selector in
 * `SegmentGlPullDataSource` (which maps SECONDARY to "Secondary Ledger" and everything else to
 * "General Ledger"), so a column reads back with the same words it was configured with.
 */
export function ledgerLabel(ledgerType?: LedgerType): string {
    switch (ledgerType) {
        case LedgerType.PRIMARY:
            return CONSTANTS.DATA_SOURCE_TYPES.GENERAL_LEDGER;
        case LedgerType.SECONDARY:
            return CONSTANTS.DATA_SOURCE_TYPES.SECONDARY_LEDGER;
        default:
            // Absent stays empty; anything else passes through verbatim rather than being folded into
            // "General Ledger". A ledger type added to the contract later is not the primary ledger, and
            // labelling it as one would state the opposite of the truth. Mirrors periodLabel.
            return ledgerType ? String(ledgerType) : '';
    }
}

/**
 * A column's display name from its id, for rendering a derived column's references. Falls back to the id,
 * which is what a reference to a column no longer in the definition leaves us with - better than a blank.
 */
export function columnDisplayName(columnId?: string, columns: ColumnDef[] = []): string {
    if (!columnId) {
        return '';
    }
    return columns.find((c) => c.columnId === columnId)?.displayName ?? columnId;
}

/**
 * The period-behaviour options a MANUAL column offers, worded from `periodBehaviorLabel` so the selector that
 * sets it and the read-only view that reports it cannot drift apart. Ordered with the carry-forward case first
 * because it is the one a user is opting into; leaving the field unset is the default and is offered as its
 * own option rather than by preselecting either behaviour.
 */
export const PERIOD_BEHAVIOR_OPTIONS = [
    PeriodBehavior.CARRY_FORWARD,
    PeriodBehavior.RESET_EACH_PERIOD,
].map((value) => {
    const labelled = periodBehaviorLabel(value);
    return { value: value as string, label: labelled?.label ?? String(value), description: labelled?.hint };
});

/**
 * A committed column's period behaviour as a short suffix for the queued-column summaries, or empty when it
 * carries none.
 *
 * Exists because columns are add-only: once saved there is no edit path, so a mis-clicked behaviour would
 * otherwise be invisible until the read-only popup on the detail view, by which point it cannot be changed.
 */
export function periodBehaviorSummary(column: ColumnDef): string {
    return periodBehaviorLabel(column.periodBehavior)?.label ?? '';
}

/**
 * Label and one-line meaning for a MANUAL column's period behaviour. The enum name alone
 * (`RESET_EACH_PERIOD`) does not say what it does to a user's data, so both are returned and the caller
 * renders the gloss beneath the label.
 */
export function periodBehaviorLabel(behavior?: PeriodBehavior): { label: string, hint: string } | undefined {
    switch (behavior) {
        case PeriodBehavior.CARRY_FORWARD:
            return { label: 'Carry forward', hint: 'Entered values persist into later periods.' };
        case PeriodBehavior.RESET_EACH_PERIOD:
            return { label: 'Reset each period', hint: 'Each period starts empty and must be entered again.' };
        default:
            // Includes absent: there is no default behaviour to claim on the column's behalf.
            return undefined;
    }
}

/** Human label for a column's source type (chooser + list). */
export function columnTypeLabel(sourceType?: ColumnSourceType): string {
    switch (sourceType) {
        case ColumnSourceType.PULL_GL:
            return 'GL Balance Pull';
        case ColumnSourceType.MANUAL:
            return 'Manual Upload';
        case ColumnSourceType.DERIVED_PCT:
            return 'Derived: % of total';
        case ColumnSourceType.DERIVED_BAL:
            return 'Derived balance';
        default:
            return String(sourceType ?? '');
    }
}

/**
 * Validate the in-progress column draft and build a `ColumnDef`, or return an error message.
 * `existingNames` is the display names of the OTHER committed columns (for the duplicate check).
 * `referenceColumns` is the columns a DERIVED column may point at (the table's existing columns plus
 * any already-queued additions); required for DERIVED_PCT / DERIVED_BAL, whose references must exist,
 * carry the expected dataType, and not themselves be derived (matches the backend
 * `SegmentColumnConfigValidator`, which keeps derivation a single hop). Emits only the fields the
 * contract stores, no stray keys.
 */
/** True for undefined / null / all-whitespace, the values that mean "no default set" rather than a number. */
function isBlank(value?: string): boolean {
    return value == null || value.trim() === '';
}

/**
 * A plain decimal, optionally signed and in scientific notation. Deliberately NOT `Number(...)`, which
 * accepts JS-only literals the backend's `new BigDecimal(...)` rejects — `Number('0x1F')` is 31 but
 * `new BigDecimal('0x1F')` throws — so a hex/octal/binary value would pass here and 417 on Save. This keeps
 * the client check in step with the server parse.
 */
const DECIMAL_NUMBER = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;

/**
 * Validates the Default Value (CBP join) input, or returns undefined when there is nothing to check.
 *
 * Blank is legitimate — it means no default — so only a non-blank value is parsed.
 */
function checkDefaultValue(value?: string): string | undefined {
    if (isBlank(value)) {
        return undefined;
    }
    return DECIMAL_NUMBER.test((value as string).trim())
        ? undefined
        : `Default value "${value}" is not a number.`;
}

export function buildColumnDef(
    draft: SegmentColumnDraft,
    existingNames: string[],
    existingIds: string[] = [],
    validCurrencies: string[] = [],
    referenceColumns: ColumnDef[] = [],
): { column?: ColumnDef; error?: string } {
    const name = (draft.displayName || '').trim();
    if (!name) {
        return { error: 'Column name is required.' };
    }
    if (existingNames.some((n) => n.trim().toLowerCase() === name.toLowerCase())) {
        return { error: `A column named "${name}" already exists.` };
    }
    // Suffixed rather than refused on collision: the display name is already known unique by the check above,
    // so a clash here is an artefact of the id spelling and not something to make the user rename around.
    const columnId = uniqueColumnId(name, existingIds);
    if (!columnId) {
        return { error: 'Column name must contain a letter or number.' };
    }

    if (draft.sourceType === ColumnSourceType.MANUAL) {
        if (!draft.dataType) {
            return { error: 'Select a data type.' };
        }
        const column: ColumnDef = {
            columnId,
            displayName: name,
            sourceType: ColumnSourceType.MANUAL,
            dataType: draft.dataType,
        };
        if (draft.dataType === ColumnDataType.NUMBER) {
            // No currency is collected or sent (see ManualColumnForm): there is no FX to apply to hand-uploaded
            // values, so the question has no answer a user could act on. A currency left on the draft by a kind
            // the user switched away from is deliberately dropped rather than stored — it would claim a unit
            // nobody chose for this column.
            //
            // This needs the matching backend change (SegmentColumnConfigValidator now requires a currency only
            // for the GL-pulling types); until that ships, a save here is refused with a 417.
            // Default value (the CBP join default) is a Number-only option. Set only when given, and only
            // if it parses as a number: an unparseable string would be stored and read back as a broken
            // default. Absent and '0' are deliberately distinct, so an empty field sets nothing.
            const defaultValueError = checkDefaultValue(draft.defaultValue);
            if (defaultValueError) {
                return { error: defaultValueError };
            }
            if (!isBlank(draft.defaultValue)) {
                column.defaultValue = (draft.defaultValue as string).trim();
            }
        }
        // Set only when chosen. The server rejects periodBehavior on any non-MANUAL column and treats absent
        // as legitimate, so an unanswered question is left unanswered rather than defaulted - a stored
        // behaviour is a claim about what happens to a finance user's numbers next period.
        if (draft.periodBehavior) {
            column.periodBehavior = draft.periodBehavior;
        }
        return { column };
    }

    if (draft.sourceType === ColumnSourceType.PULL_GL) {
        const pc = draft.pullCriteria || {};
        const criteriaError = validatePullCriteria(pc);
        if (criteriaError) {
            return { error: criteriaError };
        }
        if (!draft.currency) {
            return { error: 'Currency is required.' };
        }
        if (validCurrencies.length && !validCurrencies.includes(draft.currency)) {
            return { error: `"${draft.currency}" is not a recognized currency code. Pick one from the list.` };
        }
        const column: ColumnDef = {
            columnId,
            displayName: name,
            sourceType: ColumnSourceType.PULL_GL,
            dataType: ColumnDataType.NUMBER,
            currency: draft.currency,
            pullCriteria: buildPullCriteria(pc),
        };
        return { column };
    }

    if (draft.sourceType === ColumnSourceType.DERIVED_PCT || draft.sourceType === ColumnSourceType.DERIVED_BAL) {
        const dc = draft.derivedConfig || {};
        const refIndex: Record<string, ColumnDef> = {};
        for (const c of referenceColumns) {
            if (c && c.columnId) {
                refIndex[c.columnId] = c;
            }
        }
        // A derived reference must exist and carry the expected dataType, mirroring
        // SegmentColumnConfigValidator.requireReference/requireDataType.
        //
        // `allowDerived` relaxes the single-hop rule for one field: a derived balance may be allocated across a
        // derived percentage column, because a % of total column is a percentage like any other and is the
        // natural basis to allocate across. It stays one-directional — DERIVED_PCT's own operand must still be
        // non-derived — so the longest chain is two hops (DERIVED_BAL -> DERIVED_PCT -> non-derived) and a
        // cycle remains impossible. A derived BALANCE needs no special case: it is always NUMBER, so the
        // dataType guard below already excludes it from a field that wants PERCENTAGE.
        const checkRef = (fieldLabel: string, refId: string | undefined, wantType: ColumnDataType,
                          allowDerived = false): string | undefined => {
            if (!refId) {
                return `${fieldLabel} is required.`;
            }
            if (refId === columnId) {
                return `${fieldLabel} cannot reference the column itself.`;
            }
            const referenced = refIndex[refId];
            if (!referenced) {
                return `${fieldLabel} references unknown column "${refId}".`;
            }
            if (referenced.dataType !== wantType) {
                return `${fieldLabel} must reference a ${wantType} column, but "${refId}" is ${referenced.dataType ?? 'undefined'}.`;
            }
            if (!allowDerived
                && (referenced.sourceType === ColumnSourceType.DERIVED_PCT
                    || referenced.sourceType === ColumnSourceType.DERIVED_BAL)) {
                return `${fieldLabel} must reference a non-derived column, but "${refId}" is ${referenced.sourceType}.`;
            }
            return undefined;
        };

        if (draft.sourceType === ColumnSourceType.DERIVED_PCT) {
            // One operand, not two. A "% of total" column is each segment's share of ONE column's period
            // total (`value / SUM(column) x 100`), so that column is both the numerator and the denominator
            // and there is nothing else to name. `pctColumnId` / `valueColumnId` are deliberately not sent:
            // the write path persists the whole derivedConfig document, so a field the derivation does not
            // read would be stored and later read back as though it meant something — which is why the
            // backend refuses them outright rather than dropping them.
            const sourceErr = checkRef('Source column', dc.sourceColumnId, ColumnDataType.NUMBER);
            if (sourceErr) {
                return { error: sourceErr };
            }
            // DERIVED_PCT is always PERCENTAGE and carries no currency (the backend rejects one).
            const column: ColumnDef = {
                columnId,
                displayName: name,
                sourceType: ColumnSourceType.DERIVED_PCT,
                dataType: ColumnDataType.PERCENTAGE,
                derivedConfig: {
                    type: ColumnSourceType.DERIVED_PCT,
                    sourceColumnId: dc.sourceColumnId,
                },
            };
            return { column };
        }

        // DERIVED_BAL is a GL-pulling type: it carries its own pullCriteria AND names the percentage column its
        // aggregate is allocated across. The amount comes from that pullCriteria, so there is no source amount
        // column to name — which is why this kind sets `pctColumnId` and NOT `sourceColumnId`, the exact
        // opposite of DERIVED_PCT above.
        //
        // `pctColumnId` is not a preference: it is the only field the derivation reads.
        // `ValidateAndDeriveComponent` takes `config.path("pctColumnId")` for a DERIVED_BAL column and warns
        // "has no pctColumnId; skipping derivation" when it is absent, then `DerivationEngine.deriveBal`
        // computes `row[pctColumnId] / 100 x aggregate`. It never reads `sourceColumnId`. Sending the
        // percentage column there was refused outright by the validator ("must reference a NUMBER column but
        // ... is PERCENTAGE"), so the column could not be created at all. That percentage may itself be derived (a % of total column) —
        // see `allowDerived` on checkRef.
        const pc = draft.pullCriteria || {};
        const criteriaError = validatePullCriteria(pc);
        if (criteriaError) {
            return { error: criteriaError };
        }
        if (!draft.currency) {
            return { error: 'Currency is required.' };
        }
        if (validCurrencies.length && !validCurrencies.includes(draft.currency)) {
            return { error: `"${draft.currency}" is not a recognized currency code. Pick one from the list.` };
        }
        const pctErr = checkRef('Allocation % column', dc.pctColumnId, ColumnDataType.PERCENTAGE, true);
        if (pctErr) {
            return { error: pctErr };
        }
        const column: ColumnDef = {
            columnId,
            displayName: name,
            sourceType: ColumnSourceType.DERIVED_BAL,
            dataType: ColumnDataType.NUMBER,
            currency: draft.currency,
            pullCriteria: buildPullCriteria(pc),
            derivedConfig: {
                type: ColumnSourceType.DERIVED_BAL,
                // Only this one. `sourceColumnId` and `valueColumnId` are deliberately not sent even when the
                // draft still carries them from a kind the user switched away from: the write path persists
                // the whole derivedConfig document, so a field this derivation does not read would be stored
                // and read back as though it meant something.
                pctColumnId: dc.pctColumnId,
            },
        };
        return { column };
    }

    return { error: 'Unsupported column type.' };
}

/**
 * Build the create request from the wizard state. `tableType` is not sent: CR-299438935 dropped it
 * from the request and the backend hardcodes SEGMENT_LEVEL.
 */
export function buildCreateRequest(cw: CreateWizardState): CreateSegmentDataTableRequest {
    return {
        tableName: cw.tableName.trim(),
        segmentType: cw.segmentType as CDTSegmentType,
        ...(cw.segmentCustomCoaId ? { segmentCustomCoaId: cw.segmentCustomCoaId } : {}),
        ...(cw.refreshCadence ? { refreshCadence: cw.refreshCadence } : {}),
        columns: cw.columns,
    };
}
